import React, { useState, useEffect } from 'react';
import api from '../config/api';
import './WebhooksViewer.css';

function WebhooksViewer() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [eventFilter, setEventFilter] = useState('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [processedFilter, setProcessedFilter] = useState('all');
  const [entityIdFilter, setEntityIdFilter] = useState('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalWebhooks, setTotalWebhooks] = useState(0);

  // Webhook seleccionado para modal
  const [selectedWebhook, setSelectedWebhook] = useState(null);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Retry state
  const [retryingWebhook, setRetryingWebhook] = useState(null);

  useEffect(() => {
    fetchWebhooks();
  }, [eventFilter, entityTypeFilter, processedFilter, entityIdFilter, currentPage]);

  // Auto-refresh cada 5 segundos
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchWebhooks();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, eventFilter, entityTypeFilter, processedFilter, entityIdFilter, currentPage]);

  const fetchWebhooks = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage
      };

      if (eventFilter !== 'all') params.event = eventFilter;
      if (entityTypeFilter !== 'all') params.entityType = entityTypeFilter;
      if (processedFilter !== 'all') params.processed = processedFilter;
      if (entityIdFilter.trim()) params.entityId = entityIdFilter.trim();

      const response = await api.get('/api/webhooks', { params });
      setWebhooks(response.data.data);
      setTotalWebhooks(response.data.pagination.total);
    } catch (err) {
      setError('Error cargando webhooks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('¿Eliminar webhooks mayores a 30 días?')) return;

    try {
      const response = await api.delete('/api/webhooks/cleanup?daysOld=30');
      alert(response.data.message);
      fetchWebhooks();
    } catch (err) {
      alert('Error al limpiar webhooks');
      console.error(err);
    }
  };

  const handleRetry = async (webhookId) => {
    setRetryingWebhook(webhookId);

    try {
      const response = await api.post(`/api/webhooks/${webhookId}/retry`);
      alert(response.data.message || 'Webhook reprocesado exitosamente');
      fetchWebhooks();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Error al reprocesar webhook';
      alert(errorMessage);
      console.error(err);
    } finally {
      setRetryingWebhook(null);
    }
  };

  const totalPages = Math.ceil(totalWebhooks / itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEventBadge = (event) => {
    const [action] = event.split('.');
    const badges = {
      add: { label: 'Añadido', class: 'event-added' },
      added: { label: 'Añadido', class: 'event-added' },
      update: { label: 'Actualizado', class: 'event-updated' },
      updated: { label: 'Actualizado', class: 'event-updated' },
      delete: { label: 'Eliminado', class: 'event-deleted' },
      deleted: { label: 'Eliminado', class: 'event-deleted' }
    };
    const badge = badges[action] || { label: action, class: '' };
    return <span className={`event-badge ${badge.class}`}>{badge.label}</span>;
  };

  const getEntityBadge = (entityType) => {
    const badges = {
      deal: { label: 'Deal', class: 'entity-deal' },
      person: { label: 'Persona', class: 'entity-person' },
      organization: { label: 'Org', class: 'entity-org' },
      activity: { label: 'Actividad', class: 'entity-activity' }
    };
    const badge = badges[entityType] || { label: entityType, class: '' };
    return <span className={`entity-badge ${badge.class}`}>{badge.label}</span>;
  };

  return (
    <div className="webhooks-viewer">
      {/* Controles */}
      <div className="controls-bar">
        <div className="control-group filters-group">
          <select
            value={eventFilter}
            onChange={(e) => {
              setEventFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">Todos los eventos</option>
            <option value="add.deal">add.deal</option>
            <option value="update.deal">update.deal</option>
            <option value="delete.deal">delete.deal</option>
            <option value="add.person">add.person</option>
            <option value="update.person">update.person</option>
            <option value="delete.person">delete.person</option>
          </select>

          <select
            value={entityTypeFilter}
            onChange={(e) => {
              setEntityTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">Todas las entidades</option>
            <option value="deal">Deals</option>
            <option value="person">Personas</option>
            <option value="organization">Organizaciones</option>
            <option value="activity">Actividades</option>
          </select>

          <select
            value={processedFilter}
            onChange={(e) => {
              setProcessedFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">Todos</option>
            <option value="true">Procesados</option>
            <option value="false">Pendientes</option>
          </select>

          <input
            type="text"
            placeholder="Filtrar por ID (deal/person)..."
            value={entityIdFilter}
            onChange={(e) => {
              setEntityIdFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-input"
          />
        </div>

        <div className="control-group actions-group">
          <label className="auto-refresh-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>

          <button onClick={fetchWebhooks} className="btn-refresh" title="Recargar">
            🔄
          </button>

          <button onClick={handleCleanup} className="btn-cleanup" title="Limpiar antiguos">
            🗑️ Limpiar
          </button>
        </div>
      </div>

      {/* Lista de Webhooks */}
      {loading && webhooks.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando webhooks...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchWebhooks} className="btn btn-secondary">
            Reintentar
          </button>
        </div>
      ) : webhooks.length === 0 ? (
        <div className="empty-state">
          <p>No se encontraron webhooks</p>
          <p className="empty-hint">Los webhooks aparecerán aquí cuando Pipedrive envíe eventos</p>
        </div>
      ) : (
        <>
          <div className="webhooks-table">
            <table>
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Evento</th>
                  <th>Entidad</th>
                  <th>Entity ID</th>
                  <th>IP Origen</th>
                  <th>Fecha</th>
                  <th>Error</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((webhook) => (
                  <tr key={webhook._id} className={webhook.error ? 'webhook-error' : ''}>
                    <td>
                      <span className={`status-indicator ${webhook.processed ? 'processed' : 'pending'}`}>
                        {webhook.processed ? '✓' : '⏳'}
                      </span>
                    </td>
                    <td>{getEventBadge(webhook.event)}</td>
                    <td>{getEntityBadge(webhook.entityType)}</td>
                    <td className="entity-id">{webhook.entityId || '-'}</td>
                    <td className="source-ip">{webhook.sourceIp || '-'}</td>
                    <td className="webhook-date">{formatDate(webhook.receivedAt)}</td>
                    <td className="error-cell">
                      {webhook.error ? (
                        <span className="error-icon" title={webhook.error}>⚠️</span>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="webhook-actions">
                        <button
                          onClick={() => setSelectedWebhook(webhook)}
                          className="btn-view"
                          title="Ver JSON del webhook"
                        >
                          Ver JSON
                        </button>
                        <button
                          onClick={() => handleRetry(webhook._id)}
                          className="btn-retry"
                          disabled={retryingWebhook === webhook._id}
                          title="Reprocesar webhook con el mismo payload"
                        >
                          {retryingWebhook === webhook._id ? '⏳' : '🔄'} Reintentar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              ← Anterior
            </button>

            <span className="page-info">
              Página {currentPage} de {totalPages} ({totalWebhooks} webhooks)
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Siguiente →
            </button>
          </div>
        </>
      )}

      {/* Modal de detalle */}
      {selectedWebhook && (
        <div className="modal-overlay" onClick={() => setSelectedWebhook(null)}>
          <div className="modal-content webhook-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Webhook: {selectedWebhook.event}</h2>
                <p className="modal-subtitle">
                  {getEntityBadge(selectedWebhook.entityType)}
                  <span className="modal-id">ID: {selectedWebhook.entityId}</span>
                  <span className="modal-date">{formatDate(selectedWebhook.receivedAt)}</span>
                </p>
              </div>
              <button onClick={() => setSelectedWebhook(null)} className="btn-close">
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="webhook-info">
                <div className="info-item">
                  <strong>Estado:</strong>
                  <span className={selectedWebhook.processed ? 'text-success' : 'text-warning'}>
                    {selectedWebhook.processed ? 'Procesado' : 'Pendiente'}
                  </span>
                </div>
                <div className="info-item">
                  <strong>IP Origen:</strong>
                  <span>{selectedWebhook.sourceIp || '-'}</span>
                </div>
                {selectedWebhook.error && (
                  <div className="info-item error">
                    <strong>Error:</strong>
                    <span>{selectedWebhook.error}</span>
                  </div>
                )}
              </div>

              <div className="json-section">
                <div className="json-header">
                  <h3>Payload JSON</h3>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(selectedWebhook.payload, null, 2));
                      alert('JSON copiado al portapapeles');
                    }}
                    className="btn-copy"
                  >
                    📋 Copiar
                  </button>
                </div>
                <pre className="json-code">
                  {JSON.stringify(selectedWebhook.payload, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WebhooksViewer;
